import React, { useState, useEffect, useMemo } from 'react';
import './DeviceRulesCard.scss';
import { config } from '../../config';

// Type definitions
interface Header {
  key: string;
  sortable: boolean;
}

interface RuleData {
  Name: string;
  Association: string;
  'Rule Start': string;
  'Rule Stop': string;
  'Selected Weekdays': string;
  'Selected Weekends': string;
  Timezone: string;
}

interface ApiResponse {
  headers: Header[];
  data: RuleData[];
  records: number;
  size: number;
  start: number;
  end: number;
  page: number;
  pages: number;
}

interface SortConfig {
  key: string;
  direction: 'asc' | 'desc';
}

// Props interface
interface DeviceRulesCardProps {
  deviceId: number;
  countPerPage?: number;
}

const DeviceRulesCard: React.FC<DeviceRulesCardProps> = ({ 
  deviceId, 
  countPerPage = 2
}) => {
  // State management
  const [rulesData, setRulesData] = useState<ApiResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sortConfig, setSortConfig] = useState<SortConfig | null>(null);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');

  const API_BASE_URL = config.API_URL;

  // Fetch data from API
  useEffect(() => {
    const fetchRules = async () => {
      // Don't fetch if no deviceId is provided
      if (!deviceId) {
        setError('Device ID is required');
        setLoading(false);
        return;
      }

      // Check if API base URL is configured
      if (!API_BASE_URL) {
        setError('API configuration error: REACT_APP_API environment variable is not set');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const token = localStorage.getItem('loginToken');
        const response = await fetch(
          `${API_BASE_URL}/v2/op/device/${deviceId}/device-rules`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              'auth-token': `${token}`,
            },
          }
        );
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data: ApiResponse = await response.json();
        setRulesData(data);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An error occurred while fetching rules';
        setError(errorMessage);
        console.error('Error fetching rules:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchRules();
  }, [deviceId, API_BASE_URL]);

  // Handle sorting
  const handleSort = (key: string) => {
    if (!rulesData?.headers.find(header => header.key === key && header.sortable)) {
      return;
    }

    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Filter and sort data
  const processedData = useMemo(() => {
    if (!rulesData?.data) return [];

    let filteredData = rulesData.data.filter(rule =>
      Object.values(rule).some(value =>
        value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    if (sortConfig) {
      filteredData.sort((a, b) => {
        const aValue = a[sortConfig.key as keyof RuleData];
        const bValue = b[sortConfig.key as keyof RuleData];
        
        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filteredData;
  }, [rulesData, searchTerm, sortConfig]);

  // Pagination logic
  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * countPerPage;
    return processedData.slice(startIndex, startIndex + countPerPage);
  }, [processedData, currentPage, countPerPage]);

  const totalPages = Math.ceil(processedData.length / countPerPage);

  // Event handlers
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRetry = () => {
    setCurrentPage(1);
    setSearchTerm('');
    setSortConfig(null);
  };

  const getSortIcon = (key: string) => {
    if (!sortConfig || sortConfig.key !== key) {
      return <i className="fas fa-sort"></i>;
    }
    return sortConfig.direction === 'asc' 
      ? <i className="fas fa-sort-up"></i>
      : <i className="fas fa-sort-down"></i>;
  };

  // Format time for better display
  const formatTime = (time: string) => {
    if (!time) return '-';
    return time;
  };

  // Format weekdays display
  const formatWeekdays = (weekdays: string) => {
    if (!weekdays) return 'None';
    const days = weekdays.split(', ');
    if (days.length > 3) {
      return `${days.slice(0, 3).join(', ')}...`;
    }
    return weekdays;
  };

  // Render loading state
  if (loading) {
    return (
      <div className="card">
        <div className="card-header">
          <div className="header-content">
            <h2 className="card-title">
              <i className="fas fa-sliders-h"></i>
              Rules Applied
            </h2>
          </div>
        </div>
        <div className="card-body">
          <div className="loading-state">
            <i className="fas fa-spinner fa-spin"></i>
            <p>Loading rules...</p>
          </div>
        </div>
      </div>
    );
  }

  // Render error state
  if (error) {
    return (
      <div className="card">
        <div className="card-header">
          <div className="header-content">
            <h2 className="card-title" style={{color:'white'}}>
              Rules Applied
            </h2>
          </div>
        </div>
        <div className="card-body">
          <div className="error-state">
            <i className="fas fa-exclamation-triangle"></i>
            <p>{error}</p>
            <button 
              className="retry-btn"
              onClick={handleRetry}
            >
              <i className="fas fa-redo"></i>
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card p-0 device-rules-card">
      <div className="card-header">
        <div className="header-content">
          <h3 className="card-title">
            <i className="fas fa-sliders-h"></i>
            Rules Applied
          </h3>
        </div>
        <div className="header-actions">
          <div className="search-box">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Search rules..."
              value={searchTerm}
              onChange={handleSearchChange}
            />
          </div>
          <div className="view-toggle">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Grid View"
            >
              <i className="fas fa-th"></i>
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Table View"
            >
              <i className="fas fa-table"></i>
            </button>
          </div>
        </div>
      </div>

      <div className="card-body">
        {rulesData && rulesData.data.length > 0 ? (
          <>
            {viewMode === 'table' ? (
              // Table View
              <div className="table-responsive">
                <table>
                  <thead>
                    <tr>
                      {rulesData.headers.map((header) => (
                        <th
                          key={header.key}
                          onClick={() => header.sortable && handleSort(header.key)}
                          className={header.sortable ? 'sortable' : ''}
                        >
                          {header.key}
                          {header.sortable && getSortIcon(header.key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedData.map((rule, index) => (
                      <tr key={index}>
                        <td>
                          <div className="rule-name">
                            <i className="fas fa-rule"></i>
                            {rule.Name}
                          </div>
                        </td>
                        <td>
                          <span className={`badge ${
                            rule.Association === 'Group Rule' ? 'badge-group' : 'badge-device'
                          }`}>
                            {rule.Association}
                          </span>
                        </td>
                        <td>{rule['Rule Start']}</td>
                        <td>{rule['Rule Stop']}</td>
                        <td>{rule['Selected Weekdays']}</td>
                        <td>{rule['Selected Weekends']}</td>
                        <td>{rule.Timezone}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              // Grid View with 3 cards per row
              <div className="flex flex-wrap justify-around items-center">
                {paginatedData.map((rule, index) => (
                  <div key={index} className="rule-card">
                    <div className="rule-card-header">
                      <div className="rule-name">
                        <i className="fas fa-sliders-h"></i>
                        <h3>{rule.Name}</h3>
                      </div>
                      <span className={`badge ${
                        rule.Association === 'Group Rule' ? 'badge-group' : 'badge-device'
                      }`}>
                        {rule.Association}
                      </span>
                    </div>
                    
                    <div className="rule-card-body">
                      <div className="rule-detail">
                        <div className="detail-item">
                          <span className="detail-label">
                            <i className="fas fa-clock"></i>
                            Schedule:
                          </span>
                          <span className="detail-value">
                            {formatTime(rule['Rule Start'])} - {formatTime(rule['Rule Stop'])}
                          </span>
                        </div>
                        
                        <div className="detail-item">
                          <span className="detail-label">
                            <i className="fas fa-calendar-week"></i>
                            Weekdays:
                          </span>
                          <span className="detail-value" title={rule['Selected Weekdays']}>
                            {formatWeekdays(rule['Selected Weekdays'])}
                          </span>
                        </div>
                        
                        <div className="detail-item">
                          <span className="detail-label">
                            <i className="fas fa-calendar-day"></i>
                            Weekends:
                          </span>
                          <span className="detail-value">
                            {rule['Selected Weekends'] || 'None'}
                          </span>
                        </div>
                        
                        <div className="detail-item">
                          <span className="detail-label">
                            <i className="fas fa-globe"></i>
                            Timezone:
                          </span>
                          <span className="detail-value">
                            {rule.Timezone}
                          </span>
                        </div>
                      </div>
                    </div>
                    
                    <div className="rule-card-footer">
                      <button className="action-btn delete-btn">
                        <i className="fas fa-trash"></i>
                        Remove
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="empty-state">
            <i className="fas fa-inbox"></i>
            <p>No rules found for device {deviceId}</p>
            {searchTerm && (
              <p className="search-hint">Try adjusting your search terms</p>
            )}
          </div>
        )}
      </div>

      {rulesData && rulesData.data.length > 0 && (
        <div className="card-footer p-2">
          <div className="pagination-info">
            Showing {((currentPage - 1) * countPerPage) + 1} to{' '}
            {Math.min(currentPage * countPerPage, processedData.length)} of{' '}
            {processedData.length} entries
            {searchTerm && (
              <span> (filtered from {rulesData.records} total records)</span>
            )}
          </div>

          <div className="pagination">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Previous page"
            >
              <i className="fas fa-chevron-left"></i>
            </button>

            {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
              <button
                key={page}
                onClick={() => handlePageChange(page)}
                className={currentPage === page ? 'active' : ''}
                aria-label={`Page ${page}`}
                aria-current={currentPage === page ? 'page' : undefined}
              >
                {page}
              </button>
            ))}

            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Next page"
            >
              <i className="fas fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeviceRulesCard;